import { FC, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { KeyboardKeys, rideInvestigationRideIdAtom } from "@ryde/shared";

import { InvestigationOverlayStyled } from "./investigation.overlay-host.styled";
import { InvestigationWorkspace } from "./investigation.workspace";
import { RideInvestigationPlaybackProvider } from "./playback/ride-investigation-playback.provider";
import { useResetInvestigationSelections } from "./use-reset-investigation-selections";

/**
 * The single mount point for Deep Ride Investigation. It is rendered once at the app root and self-gates on
 * the ride id, so any entry point — the live-map toolbar or the ride edit drawer — only has to write that
 * atom. Being a full-screen sibling of the drawers container, it opens on top of an open ride drawer instead
 * of replacing the page underneath.
 *
 * The `null` return when closed is load-bearing, twice over: the playback provider (and therefore the
 * investigation + tracking fetches) never runs on unrelated pages, and the map is unmounted rather than
 * hidden — a MapLibre canvas created inside a `display: none` host is born 0x0 and never recovers.
 */
export const InvestigationOverlayHost: FC = () => {
  const rideId = useRecoilValue(rideInvestigationRideIdAtom);
  const setRideId = useSetRecoilState(rideInvestigationRideIdAtom);
  const { pathname } = useLocation();

  const resetSelections = useResetInvestigationSelections();

  const close = useEvent(() => {
    setRideId(undefined);
  });

  /*
   * The layer filters are reset HERE, in the host, because the host is the only part of the feature that
   * outlives a close. This used to sit in the playback provider, which renders below the `rideId === undefined`
   * gate below — so it unmounted in the same commit that cleared the id and could never observe the close.
   * The effect was dead code and every toggle the user turned off carried into the next investigation.
   * Keyed on the id (not on `close`) so it also covers the paths that clear the atom directly: the panel's ✕
   * and the toolbar button's toggle.
   */
  useEffect(() => {
    if (rideId === undefined) {
      resetSelections();
    }
  }, [rideId, resetSelections]);

  // Nothing else resets the ride id, and a global atom would otherwise resurface the overlay over an
  // unrelated page after navigation.
  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(() => {
    if (rideId === undefined) {
      return;
    }

    // Capture phase, and stop the event dead: the drawer listens for Escape on the bubble phase to run its
    // unsaved-changes confirm, and closing the overlay must not close or interrogate the drawer beneath it.
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== KeyboardKeys.ESCAPE) {
        return;
      }

      close();
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [rideId, close]);

  if (rideId === undefined) {
    return null;
  }

  return (
    <InvestigationOverlayStyled>
      <RideInvestigationPlaybackProvider>
        <InvestigationWorkspace />
      </RideInvestigationPlaybackProvider>
    </InvestigationOverlayStyled>
  );
};
